<template>
  <div :class="{ 'no-background': voucherTransferredOrCanceled || (!hasBonusAndPaymentAmount && !voucherMainPropsHasRules) }" class="voucher-amount">
    <div class="main-section">
      <voucher-base-amount :text="voucherMainProps.text" :amount="voucherMainProps.amount" :amount-style-large="true" />
    </div>

    <div v-if="!voucherTransferredOrCanceled && (hasBonusAndPaymentAmount || voucherMainPropsHasRules)" class="secondary-section">
      <voucher-base-amount v-if="voucherMainPropsHasRules" :rules="voucherMainProps.rules" />

      <voucher-base-amount v-if="showAmountDetails" v-bind="voucherContributionProps" />
      <voucher-base-amount v-if="showAmountDetails" v-bind="voucherBonusProps" />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

import VoucherBaseAmount from './VoucherBaseAmount.vue';
import moment from 'moment-timezone';

export default {
  components: { VoucherBaseAmount },
  name: 'voucher-amount',
  props: {
    warningColor: {
      type: String,
      default: 'var(--color-text-color)',
    },
    voucher: {
      type: Object,
    },
  },
  computed: {
    ...mapGetters('ui', ['currentLocale']),

    voucherTransferredOrCanceled() {
      return this.voucher.status === 'transferred' || this.voucher.status === 'canceled' || this.voucher.pending_transfer;
    },
    showAmountDetails() {
      return this.voucher.bonus_amount > 0 && this.voucher.payment_amount > 0;
    },
    isBonusAmountOnly() {
      return this.voucher.payment_amount === 0 && this.voucher.bonus_amount > 0;
    },
    totalAmount() {
      return this.voucher.remaining_amount;
    },
    bonusAmount() {
      return this.voucher.remaining_bonus_amount;
    },
    paymentAmount() {
      return this.voucher.remaining_payment_amount;
    },
    isPaymentOnly() {
      return this.voucher.payment_amount > 0 && this.voucher.bonus_amount === 0;
    },
    hasBonusAndPaymentAmount() {
      return this.voucher.payment_amount > 0 && this.voucher.bonus_amount > 0;
    },
    hasExpiration() {
      return !!this.voucher.bonus_expiration_date;
    },
    isExpired() {
      return moment().isAfter(this.voucher.bonus_expiration_date);
    },
    isDormancyApplied() {
      return !!this.voucher.dormancy.last_fee_date;
    },
    hasDormancyFee() {
      return this.voucher.payment_amount > 0 && _get(this.voucher, 'dormancy.enabled', false);
    },
    hasClosureDate() {
      return !!this.voucher.closure_date;
    },
    voucherMainPropsHasRules() {
      return !_isEmpty(this.voucherMainProps.rules);
    },
    voucherMainProps() {
      if (this.voucher.status === 'transferred' || this.voucher.pending_transfer) {
        return {
          amount: this.voucher.payment_amount + this.voucher.bonus_amount,
          boldAmount: true,
        };
      }
      if (this.isBonusAmountOnly) {
        const rules = [];

        if (this.hasExpiration) {
          rules.push({
            text: this.$t(this.isExpired ? 'components.account_voucher.bonus_expired' : 'components.account_voucher.bonus_expiration', { date: this.$options.filters.formatShortDate(this.voucher.bonus_expiration_date) }),
            textColor: this.isExpired ? this.warningColor : 'var(--color-text-primary)',
          });
        }

        return {
          text: this.$t('components.account_voucher.actual_balance'),
          amount: this.totalAmount,
          rules,
          boldAmount: true,
        };
      }
      if (this.isPaymentOnly) {
        const rules = [];

        if (this.hasDormancyFee) {
          const dormancyAmount = this.$options.filters.currency_full(this.voucher.dormancy.settings.fixed_fee);
          const amount = this.$options.filters.currencySymbol(this.$options.filters.currency(dormancyAmount));

          rules.push({
            hideHint: false,
            text: this.$t(this.isDormancyApplied ? 'components.account_voucher.dormancy.dormancy_applied_sub_text' : 'components.account_voucher.dormancy.dormancy_sub_text', {
              date: this.$options.filters.formatShortDateWithoutDays(this.voucher.dormancy.start_date),
            }),
            textColor: this.isDormancyApplied ? this.warningColor : 'var(--color-text-primary)',
            hintLinkText: this.$t('components.account_voucher.dormancy.dormancy_hints.link_title'),
            hintTitle: this.$t('components.account_voucher.dormancy.dormancy_hints.title'),
            hintHtmlMessage: this.$t('components.account_voucher.dormancy.dormancy_hints.message', {
              date: this.$options.filters.formatTextDate(this.voucher.dormancy.start_date),
              amount,
              delay: this.voucher.dormancy.settings.last_activity_trigger_in_months,
            }),
          });
        } else {
          if (this.hasClosureDate) {
            rules.push({
              text: this.$t('components.account_voucher.my_money_transfer', { date: this.$options.filters.formatShortDate(this.voucher.closure_date) }),

              hintLinkText: this.$t('components.account_voucher.transfer_hints.link_title'),
              hintTitle: this.$t('components.account_voucher.transfer_hints.title'),
              hintHtmlMessage: this.$t('components.account_voucher.transfer_hints.message', { date: this.$options.filters.formatTextDate(this.voucher.closure_date) }),
            });
          }
        }
        return {
          text: this.$t('components.account_voucher.actual_balance'),
          amount: this.totalAmount,
          boldAmount: true,
          rules,
        };
      }

      return {
        text: this.$t('components.account_voucher.actual_balance'),
        amount: this.totalAmount,
        boldAmount: true,
      };
    },
    voucherContributionProps() {
      const rules = [];

      if (this.hasDormancyFee) {
        const dormancyAmount = this.$options.filters.currency_full(this.voucher.dormancy.settings.fixed_fee);
        const amount = this.$options.filters.currencySymbol(this.$options.filters.currency(dormancyAmount));

        rules.push({
          text: this.$t(this.isDormancyApplied ? 'components.account_voucher.dormancy.dormancy_applied_sub_text' : 'components.account_voucher.dormancy.dormancy_sub_text', {
            date: this.$options.filters.formatShortDateWithoutDays(this.voucher.dormancy.start_date),
          }),
          textColor: this.isDormancyApplied ? this.warningColor : 'var(--color-text-primary)',
          hideHint: false,
          hintLinkText: this.$t('components.account_voucher.dormancy.dormancy_hints.link_title'),
          hintTitle: this.$t('components.account_voucher.dormancy.dormancy_hints.title'),
          hintHtmlMessage: this.$t('components.account_voucher.dormancy.dormancy_hints.message', {
            date: this.$options.filters.formatTextDate(this.voucher.dormancy.start_date),
            amount,
            delay: this.voucher.dormancy.settings.last_activity_trigger_in_months,
          }),
        });
      } else {
        if (this.hasClosureDate) {
          rules.push({
            text: this.$t('components.account_voucher.my_money_transfer', { date: this.$options.filters.formatShortDate(this.voucher.closure_date) }),
            hideHint: false,
            hintLinkText: this.$t('components.account_voucher.contribution_hints.link_title'),
            hintTitle: this.$t('components.account_voucher.contribution_hints.title'),
            hintHtmlMessage: this.$t('components.account_voucher.contribution_hints.message', { date: this.$options.filters.formatTextDate(this.voucher.closure_date) }),
          });
        }
      }

      return {
        text: this.$t('components.account_voucher.contribution'),
        amount: this.hasBonusAndPaymentAmount ? this.paymentAmount : null,
        rules,
      };
    },
    voucherBonusProps() {
      const rules = [];

      if (this.hasExpiration) {
        rules.push({
          text: this.$t(this.isExpired ? 'components.account_voucher.bonus_expired' : 'components.account_voucher.bonus_expiration', { date: this.$options.filters.formatShortDate(this.voucher.bonus_expiration_date) }),
          textColor: this.isExpired ? this.warningColor : 'var(--color-text-primary)',
          hintLinkText: this.$t('components.account_voucher.bonus_hints.link_title'),
          hintTitle: this.$t('components.account_voucher.bonus_hints.title'),
          hintHtmlMessage: this.$t('components.account_voucher.bonus_hints.message', { date: this.$options.filters.formatTextDate(this.voucher.bonus_expiration_date) }),
        });
      }

      return {
        text: this.$t('components.account_voucher.bonus'),
        amount: this.hasBonusAndPaymentAmount ? this.bonusAmount : null,
        rules,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.voucher-amount {
  width: 100%;
  border: var(--border-thin) solid var(--color-border);

  @media ($sm-min) {
    max-width: 445px;
  }
}

.main-section {
  padding: var(--gap);
  background-color: #f2f2f2;
}

.voucher-amount.no-background {
  border: none;
}
.secondary-section {
  padding: var(--gap);
}
</style>
